import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link, Outlet, useLocation } from 'react-router';

import {
  activeLink,
  activeSection,
  homeArea,
  sectionsFor,
  SUPERADMIN,
  type Area,
  type NavLink,
} from '../app/navigation';
import { brand } from '../lib/brand';
import { useMe } from '../lib/me';
import { OrgIdentity } from './OrgIdentity';
import { SupportBanner } from './SupportBanner';
import { UserMenu } from './UserMenu';

const SIDEBAR_WIDTH = 220;

/** Without an area, as on the account pages, it uses the area this person's role calls home. */
export function AppShell({ area }: { area?: Area }) {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  if (!me) return null;

  const shownArea = area ?? homeArea(me.role);
  const sections = sectionsFor(shownArea, me.permissions);
  const section = activeSection(sections, pathname);
  const link = activeLink(me.is_platform_admin ? [...sections, SUPERADMIN] : sections, pathname);
  const sideList = (links: NavLink[]) => (
    <List sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, overflowX: 'auto' }}>
      {links.map((l) => (
        <ListItemButton
          key={l.to}
          component={Link}
          to={l.to}
          selected={l.to === link?.to}
          aria-current={l.to === link?.to ? 'page' : undefined}
          sx={{ flexGrow: 0, flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          <ListItemText primary={l.label} />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <AppBar position="static" elevation={0}>
        <Toolbar
          disableGutters
          sx={{ alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}
        >
          {/* The same width as the sidebar, so its line carries on up through the bar. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              boxSizing: 'border-box',
              width: { xs: '100%', md: SIDEBAR_WIDTH },
              px: 2,
              py: { xs: 1, md: 0 },
              bgcolor: 'primary.dark',
            }}
          >
            <Typography variant="h6" noWrap>
              {brand.name}
            </Typography>
          </Box>
          {/* The same left edge as the page below it. */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'stretch',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              columnGap: 2,
              px: { xs: 2, sm: 3 },
            }}
          >
            {shownArea !== 'platform' && <OrgIdentity me={me} />}
            <Box
              component="nav"
              aria-label="Sections"
              sx={{
                order: { xs: 3, md: 0 },
                width: { xs: '100%', md: 'auto' },
                flexShrink: 1,
                minWidth: 0,
                display: 'flex',
                overflowX: 'auto',
                scrollbarWidth: 'none',
              }}
            >
              {sections.map((s) => (
                <Button
                  key={s.label}
                  color="inherit"
                  component={Link}
                  to={s.links[0].to}
                  aria-current={s === section ? 'true' : undefined}
                  sx={{
                    flexShrink: 0,
                    minWidth: 0,
                    px: 1.5,
                    minHeight: 48,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: s === section ? 600 : 400,
                    borderRadius: 0,
                    borderBottom: 3,
                    borderColor: s === section ? 'secondary.main' : 'transparent',
                  }}
                >
                  {s.label}
                </Button>
              ))}
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <UserMenu me={me} />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <SupportBanner me={me} />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            width: { md: SIDEBAR_WIDTH },
            bgcolor: 'background.paper',
            borderRight: { md: 1 },
            borderBottom: { xs: 1, md: 0 },
            // Per breakpoint, because the responsive borders above set a color of their own.
            borderColor: { xs: 'divider', md: 'divider' },
          }}
        >
          <Box component="nav" aria-label={section?.label}>
            {section && sideList(section.links)}
          </Box>
          {me.is_platform_admin && (
            <Box
              component="nav"
              aria-label={SUPERADMIN.label}
              sx={{ mt: { md: 'auto' }, position: { md: 'sticky' }, bottom: { md: 0 } }}
            >
              <Divider />
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ px: 2, display: 'block', pt: 1 }}
              >
                {SUPERADMIN.label}
              </Typography>
              {sideList(SUPERADMIN.links)}
            </Box>
          )}
        </Box>
        {/* Not centered, so the page always starts under the business name. */}
        <Container component="main" maxWidth="xl" sx={{ py: 3, minWidth: 0, ml: 0 }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
